import { APP_INITIALIZER, makeEnvironmentProviders, NgModule, Optional, SkipSelf, TransferState } from '@angular/core'
import { RootComponent } from 'src/app/root/root.component'
import { RuntimeService } from 'src/app/service/runtime.service'
import { RedirectorService } from 'src/app/service/redirector.service'
import { RouterModule, RouterOutlet, UrlSerializer } from '@angular/router'
import { APP_BASE_HREF, NgOptimizedImage } from '@angular/common'
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { BrowserModule } from '@angular/platform-browser'
import { MatDialogModule } from '@angular/material/dialog'
import { IconComponent } from 'src/app/elements/icon/icon.component'
import { PriceComponent } from 'src/app/elements/price/price.component'
import { AccordionComponent } from 'src/app/elements/accordion/accordion.component'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { SafePipe } from 'src/app/pipes/safe.pipe'
import { ContentBlockComponent } from 'src/app/elements/content-block/content-block.component'
import { ButtonComponent } from 'src/app/elements/button/button.component'
import { TaxNoteComponent } from 'src/app/elements/tax-note/tax-note.component'
import { I18nSelectorComponent } from 'src/app/elements/i18n-selector/i18n-selector.component'
import { AutosizeIframeDirective } from 'src/app/directives/autosize-iframe.directive'
import { MetaService } from 'src/app/service/meta/meta.service'
import { InlineSVGModule } from 'ng-inline-svg-2'
import { TooltipComponent } from 'src/app/elements/tooltip/tooltip.component'
import { PaymentMethodsComponent } from 'src/app/elements/payment-methods/payment-methods.component'
import { PageService } from 'src/app/service/page.service'
import { ForsaleDefaultComponent } from 'src/app/buy-page/forsale/forsale-default/forsale-default.component'
import { ForsaleExpertComponent } from 'src/app/buy-page/forsale/forsale-expert/forsale-expert.component'
import { ForsaleSmartComponent } from 'src/app/buy-page/forsale/forsale-smart/forsale-smart.component'
import { PaynowSicherheitComponent } from 'src/app/buy-page/paynow/paynow-sicherheit/paynow-sicherheit.component'
import { PaynowVertriebComponent } from 'src/app/buy-page/paynow/paynow-vertrieb/paynow-vertrieb.component'
import {
    ED_INJECT_APP_BASE_HREF,
    ED_INJECT_DOMAIN,
    ED_INJECT_EXPRESS_REQUEST,
    ED_INJECT_EXPRESS_RESPONSE,
    ED_INJECT_HOST,
    ED_INJECT_LOCALE_ID,
    ED_STORE_APP_BASE_HREF,
    ED_STORE_LOCALE_ID,
} from 'src/server/server.data'
import { DomService } from 'src/app/service/dom.service'
import { routes } from 'src/app/app.routes'
import { Environment } from 'src/environments/environment'
import { Request } from 'express'
import { ImprintComponent } from 'src/app/pages/imprint/imprint.component'
import { CustomUrlSerializer } from 'src/app/service/custom-url-serializer'
import { SourceService } from 'src/app/service/source.service'

const providers = []
if (!Environment.production) {
  //const devHost = 'sss.de'
  const devHost = 'buyme.de'

  // mock injects for local development
  providers.push(makeEnvironmentProviders([
    {
      provide: ED_INJECT_EXPRESS_REQUEST, useValue: {
        originalUrl: '/de/kaiserkraut.de',
        protocol: 'http',
        hostname: devHost,
      } as Partial<Request>
    },
    { provide: ED_INJECT_EXPRESS_RESPONSE, useValue: {} },
    { provide: ED_INJECT_HOST, useValue: devHost },
    { provide: ED_INJECT_DOMAIN, useValue: devHost },
  ]))
}

@NgModule({
    declarations: [
        RootComponent,
    ],
    bootstrap: [RootComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        RouterOutlet,
        MatDialogModule,
        IconComponent,
        PriceComponent,
        AccordionComponent,
        NgOptimizedImage,
        SafePipe,
        ContentBlockComponent,
        ButtonComponent,
        TaxNoteComponent,
        I18nSelectorComponent,
        AutosizeIframeDirective,
        RouterModule,
        RouterModule.forRoot(routes),
        InlineSVGModule,
        TooltipComponent,
        PaymentMethodsComponent,
        ForsaleDefaultComponent,
        ForsaleExpertComponent,
        ForsaleSmartComponent,
        PaynowSicherheitComponent,
        PaynowVertriebComponent,
        ImprintComponent,
    ],
    providers: [
        ...providers,
        RuntimeService,
        RedirectorService,
        MetaService,
        PageService,
        SourceService,
        DomService,
        {
            provide: ED_INJECT_LOCALE_ID,
            useFactory: (state: TransferState, locale?: string) => {
                if (locale) {
                    state.set(ED_STORE_LOCALE_ID, locale);
                }
                return locale ?? state.get(ED_STORE_LOCALE_ID, 'en');
            },
            deps: [TransferState, [new Optional(), new SkipSelf(), ED_INJECT_LOCALE_ID]],
        },
        {
            provide: APP_BASE_HREF,
            useFactory: (state: TransferState, baseHref?: string) => {
                if (baseHref) {
                    state.set(ED_STORE_APP_BASE_HREF, baseHref)
                }
                return baseHref ?? state.get(ED_STORE_APP_BASE_HREF, '')
            },
            deps: [TransferState, [new Optional(), new SkipSelf(), ED_INJECT_APP_BASE_HREF]],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (redirectorService: RedirectorService, pageService: PageService, sourceService: SourceService) => {
                return async () => {
                    await redirectorService.boot()
                    pageService.boot()
                    sourceService.boot()
                }
            },
            deps: [RedirectorService, PageService, SourceService, RuntimeService],
            multi: true,
        },
        {
            provide: UrlSerializer,
            useClass: CustomUrlSerializer,
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {}
